/**
 * PaneDividers — draggable seams between a chart group's stacked panes.
 *
 * An HTML overlay rather than a SciChart affordance, for two reasons: the panes
 * are SubCharts inside a single WebGL canvas with nothing between them to grab,
 * and a DOM handle is the same interaction `SectionGrid` already uses for layout
 * tracks — one resize idiom in the app rather than two.
 *
 * **The gotcha this is built around:** SciChart's `ZoomPanModifier` listens on
 * the canvas, so a pointer-down that reaches it starts a pan instead of a drag,
 * and the chart lurches sideways while you resize. The handle therefore stops
 * propagation and captures the pointer, which also keeps the drag alive when the
 * cursor leaves the thin hit area.
 *
 * Geometry comes from `paneBoundaries` in `@qufolio/chart-engine` — the same
 * function the renderer stacks with, so a handle cannot drift from its seam.
 */

import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { Pane } from "@qufolio/model";
import {
  MIN_PANE_PX,
  paneBoundaries,
  paneWeights,
  resizeAtBoundary,
} from "@qufolio/chart-engine";

export interface PaneDividersProps {
  panes: readonly Pane[];
  /** Live during the drag, so the chart tracks the pointer. */
  onResize: (heights: string[]) => void;
  /** Fired once on release, for the workspace write. */
  onCommit?: (heights: string[]) => void;
}

export function PaneDividers({ panes, onResize, onCommit }: PaneDividersProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const latest = useRef<string[] | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  // Tracked separately from hover: the pointer is captured during a drag, so it
  // can leave the 9px hit area while the seam is still being moved and the line
  // should stay lit.
  const [dragging, setDragging] = useState<number | null>(null);

  const weights = paneWeights(panes);
  const boundaries = paneBoundaries(weights);

  const startDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, boundary: number) => {
      // Both matter: preventDefault stops text selection, stopPropagation keeps
      // the event off SciChart's ZoomPanModifier so the chart doesn't pan.
      event.preventDefault();
      event.stopPropagation();

      const host = hostRef.current;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      if (rect.height === 0) return;

      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      latest.current = null;
      setDragging(boundary);

      // **The floor is a pixel height, and only this side can say what that is
      // in shares.** `resizeAtBoundary` is pure and sees weights, not a
      // container; `rect` is already measured here for the drag itself, so the
      // conversion costs nothing and the floor stops being 6% of however tall
      // the chart happens to be.
      const minShare = MIN_PANE_PX / rect.height;

      const move = (ev: PointerEvent) => {
        const fraction = (ev.clientY - rect.top) / rect.height;
        const heights = resizeAtBoundary(weights, boundary, fraction, minShare);
        latest.current = heights;
        onResize(heights);
      };

      const up = () => {
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", up);
        // One workspace write per drag, not one per pixel — the same reason the
        // grid only persists a column resize when `finished` is true.
        if (latest.current) onCommit?.(latest.current);
        latest.current = null;
        setDragging(null);
      };

      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", up);
    },
    [weights, onResize, onCommit],
  );

  // One seam short of the pane count: the outer edges are the container.
  if (boundaries.length === 0) return null;

  return (
    <div
      ref={hostRef}
      style={{
        position: "absolute",
        inset: 0,
        // The overlay itself must not swallow chart interaction; only the
        // handles below opt back in.
        pointerEvents: "none",
      }}
    >
      {boundaries.map((y, i) => (
        <div
          key={i}
          onPointerDown={(e) => startDrag(e, i)}
          onPointerEnter={() => setHovered(i)}
          onPointerLeave={() => setHovered((h) => (h === i ? null : h))}
          title="Drag to resize panes"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `${y * 100}%`,
            // The hit area stays 9px — comfortably grabbable — while the line
            // drawn inside it is 1px, so the seam reads as a hairline rather
            // than a band.
            height: 9,
            transform: "translateY(-50%)",
            cursor: "row-resize",
            pointerEvents: "auto",
            touchAction: "none",
            zIndex: 6,
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              height: hovered === i || dragging === i ? 2 : 1,
              background: hovered === i || dragging === i ? "var(--qf-accent)" : "var(--qf-border)",
              transition: "background 120ms, height 120ms",
            }}
          />
        </div>
      ))}
    </div>
  );
}
